import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { StyleSheet, css } from 'aphrodite';
import { AutoComplete, Modal, Typography, Button, Tooltip } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, SearchOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { FETCH_PHARMACY_EMAIL_LOGS, GET_PHARMACY_EMAIL_LOG_FILE, UPDATE_EMAIL_LOG_SEEN_STATUS } from '../../graphql/pharmacyEmailLogs';
import moment from 'moment';
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const sx = StyleSheet.create({
  emailLogAttachment: {
    color: '#5009b5',
    marginLeft: 10,
  },
  emailLogContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 5,
    width: '90%',
  },
  emailLogTextContainer: {
    fontSize: 14,
  },
  emailLogSeenIcon: {
    border: 'none',
    color: '#231E33',
    fontSize: 20,
  },
  modalContainer: {
    minWidth: '75%',
    minHeight: '90%',
  },
  description: {
    color: '#231E33',
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 30,
    marginTop: 25,
  },
  buttonCancel: {
    border: '1px solid #5009b5',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '400',
    padding: '8px 40px',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: '400px',
    maxWidth: '500px',
    height: '40px',
    marginRight: '12px',
    marginTop: 10,
    border: '1px solid #5009b5',
    borderRadius: '8px',
  },
  searchInputContainer: {
    flexBasis: '90%',
    fontSize: '14px',
  },
  searchBarIcon: {
    flexBasis: '10%',
    flexShrink: '0',
    border: 'none',
    background: 'none',
    borderLeft: '1px solid #5009b5',
    borderRadius: '0',
  },
  title: {
    color: '#231E33',
    fontWeight: '500',
    fontSize: 24,
  },
  scrollable: {
    height: 400,
    overflowY: 'auto',
  },
  list: {
    listStyleType: 'none',
    padding: '0',
    margin: '0',
  },
});

const { Option } = AutoComplete;

const { Title, Text, Link } = Typography;

const AutoRxEmailLogsModal = ({ onSaveCancel, pharmacyEmailAddress, visible }) => {
  const [search, setSearch] = useState('');
  const [emailLogs, setEmailLogs] = useState([]);

  const [getEmailLogs, { loading: isGettingEmailLogs }] = useLazyQuery(
    FETCH_PHARMACY_EMAIL_LOGS,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      onCompleted: ({ fetchPharmacyEmailLogs }) => {
        setEmailLogs(fetchPharmacyEmailLogs.emailLogs);
      },
    },
  );

  // TODO: Commenting out for now as it is not tagged for 10/30/2025 release, but will be used
  // in an upcoming release during Q1 2026
  // const [updateEmailLogSeenStatus, { loading: updateEmailLogSeenStatusLoading }] = useMutation(
  //   UPDATE_EMAIL_LOG_SEEN_STATUS,
  //   {
  //     onCompleted: () => {
  //       const variables = {
  //         emailId: pharmacyEmailAddress.email,
  //         pharmacyId: pharmacyEmailAddress.pharmacy_id,
  //       };
  //       getEmailLogs({ variables });
  //     },
  //   },
  // );

  const [getEmailLogFile, { loading: isGettingEmailLogFile }] = useLazyQuery(
    GET_PHARMACY_EMAIL_LOG_FILE,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      onCompleted: async ({ getPharmacyEmailLogFile }) => {
        try {
          if (!getPharmacyEmailLogFile?.file_content) {
            console.error("File content is missing or invalid");
            return;
          }
          // Create a temporary container to render the HTML content
          const tempContainer = document.createElement("div");
          tempContainer.style.position = "absolute";
          tempContainer.style.left = "-9999px"; // Hide it off-screen
          tempContainer.innerHTML = getPharmacyEmailLogFile.file_content;
          document.body.appendChild(tempContainer);

          // Use html2canvas to render the content as an image
          html2canvas(tempContainer).then((canvas) => {
            const imgData = canvas.toDataURL("image/png");

            // Generate a PDF and add the image
            const pdf = new jsPDF();
            const imgWidth = 190; // Fit within the page width
            const pageHeight = pdf.internal.pageSize.height;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let position = 0;
            // Add the image to the PDF, handling page breaks if necessary
            if (imgHeight > pageHeight) {
              let remainingHeight = imgHeight;

              while (remainingHeight > 0) {
                pdf.addImage(imgData, "PNG", 10, position, imgWidth, pageHeight);
                remainingHeight -= pageHeight;
                position = 0;
                if (remainingHeight > pageHeight) pdf.addPage();              }
            } else {
              pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
            }
            // Clean up the temporary container
            document.body.removeChild(tempContainer);

            // Open the generated PDF in a new tab
            const pdfBlob = pdf.output("blob");
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, "_blank");
          });
        } catch (error) {
          console.error("Error in onCompleted handler:", error);
        }
      },
    }
  );

  const onPressAttachment = (emailLog) => {
    const variables = {
      id: emailLog.id,
      pharmacy_id: pharmacyEmailAddress.pharmacy_id,
    };
    getEmailLogFile({ variables });
  };

  // TODO: Commenting out for now as it is not tagged for 10/30/2025 release, but will be used
  // in an upcoming release during Q1 2026
  // const emailSeenStatusUpdate = (email) => {
  //   const variables = {
  //     logId: email.id,
  //     isSeen: true,
  //   };
  //   updateEmailLogSeenStatus({ variables });
  // };

  const formatEmailSubject = (subject) => {
    if(!subject){
      return '';
    };
    const formattedSubject = subject.replace('<SECURE>', '').trim();
    return formattedSubject;
  };

  useEffect(() => {
    if (!!pharmacyEmailAddress) {
      const variables = {
        emailId: pharmacyEmailAddress.email,
        pharmacyId: pharmacyEmailAddress.pharmacy_id,
      };
      getEmailLogs({ variables });
    }
  }, [pharmacyEmailAddress]);

  useEffect(() => {
    setFilteredEmailLogs(emailLogs);
  }, [emailLogs]);

  const [filteredEmailLogs, setFilteredEmailLogs] = useState([])

  const onSubmitSearch = () => {
    if (!search) {
      setFilteredEmailLogs(emailLogs);
    } else {
      const emailsByText = searchByEmailSubject();
      const emailsByDate = searchByEmailDate();
      if (emailsByText.length > 0) {
        setFilteredEmailLogs(emailsByText)
      } else if (emailsByDate.length > 0) {
        setFilteredEmailLogs(emailsByDate)
      } else {
        setFilteredEmailLogs([]);
      }
    }
  }

  const onSubmitSearchOnKey = (keyEntered) => {
    if (keyEntered.key === 'Enter') {
      onSubmitSearch();
    }
  }

  const onChangeSearchText = (searchText) => {
    setSearch(searchText);
    if (!searchText) {
      setFilteredEmailLogs(emailLogs);
    };
  };

  const searchByEmailSubject = () => {
    const filteredEmailLogs = emailLogs?.filter(email => {
      if (email.email_subject.toLocaleLowerCase().includes(search.toLocaleLowerCase())) {
        return true;
      }
      return false
    }) ?? [];
    return filteredEmailLogs;
  };

  const searchByEmailDate = () => {
    const filteredEmailLogs = emailLogs?.filter(email => {
      if (moment(parseInt(email.email_date, 10)).format('MM/DD/YYYY').includes(search)) {
        return true;
      }
      return false
    }) ?? []
    return filteredEmailLogs;
  }


  return (
    <Modal
      onCancel={onSaveCancel}
      open={visible}
      className={css(sx.modalContainer)}
      footer={[
        <Button key="cancel" onClick={() => onSaveCancel('asc')} className={css(sx.buttonCancel)}>
          Close
        </Button>
      ]}
    >
      <Text className={css(sx.title)}>Auto Rx Email Logs</Text>
      <div
        className={css(sx.searchContainer)}
      >
        <AutoComplete
          bordered={false}
          className={css(sx.searchInputContainer)}
          placeholder="Search Logs"
          dropdownMatchSelectWidth={false}
          dropdownStyle={{ width: '300px', zIndex: '900' }}
          value={search}
          defaultActiveFirstOption={false}
          onChange={onChangeSearchText}
          onKeyDown={e => onSubmitSearchOnKey(e)}
        />
        <Tooltip title="Search for Email Logs">
          <Button
            className={css(sx.searchBarIcon)}
            icon={<SearchOutlined />}
            onClick={onSubmitSearch}
          />
        </Tooltip>
      </div>
      <div className={css(sx.description)}>
        <Text>
          {`${filteredEmailLogs?.length ?? 0} TOTAL LOGS`}
        </Text>
      </div>
      <div className={css(sx.scrollable)}>
        <ul className={css(sx.list)}>
          {filteredEmailLogs?.map((pharmacyEmail, index) => {
            return (
              <li key={pharmacyEmail.id}>
                <div className={css(sx.emailLogContainer)}>
                  <div className={css(sx.emailLogTextContainer)}>
                    <Text>{`${index + 1}. ${formatEmailSubject(pharmacyEmail.email_subject)}`}</Text>
                    {!!pharmacyEmail?.file_name && (
                      <Link 
                        className={css(sx.emailLogAttachment)} 
                        onClick={() => {
                        onPressAttachment(pharmacyEmail);
                        }}
                        underline
                      >
                        {`(${pharmacyEmail?.file_name})`}
                      </Link>
                    )}
                  </div>
                  {/* TODO: Commenting for now, will be enabled in a future release sometime during Q1 2026 */}
                  {/* {pharmacyEmail.is_seen ? <EyeOutlined className={css(sx.emailLogSeenIcon)}  /> : <EyeInvisibleOutlined className={css(sx.emailLogSeenIcon)}  />} */}
                </div>
              </li>);
          })}
        </ul>
      </div>
    </Modal>
  );
};

AutoRxEmailLogsModal.propTypes = {
  onSaveCancel: PropTypes.func,
  pharmacyEmailAddress: PropTypes.object,
  visible: PropTypes.bool,
};

export default AutoRxEmailLogsModal;
